import {
  ColDef,
  ProcessCellForExportParams,
  ValueFormatterFunc,
  ValueFormatterParams,
  ValueGetterFunc,
} from '@ag-grid-community/core';
import { ITooltipParams } from '@ag-grid-community/core/dist/cjs/es5/rendering/tooltipComponent';
import { ContentType, PermissionEnum, UserPermissions } from '@creditsights/api-ui-web';
import { agTableValueComparator, Icon, Loader } from '@creditsights/styleguide-web';
import React from 'react';
import { NavLink } from 'react-router-dom';
import { fetchScoresCategory, getScoreState } from '../../utils/scoreUtils';
import { EM_DASH } from '../../Base.constants';
import { CS_VIEW_DATA } from '../../components/Filters/Filters.constants';
import { getDefaultRpColumnDefByRenderType } from '../../components/widgets/Dashboard/Monitor/Monitor.logic';
import { RpColumn, RpColumnRenderType } from '../../models/RpColumn';
import { IssuerDto } from '../../services/dtos/issuer/issuer.dto';
import { IssuerListItemDto } from '../../services/dtos/issuer/issuerList.dto';
import { EsField } from '../../services/dtos/riskProducts/rpColumn.dto';
import { DATE_FORMATS, formatDate } from '../../utils/dateUtils';
import CoreIcon from '../../components/widgets/CoreScore/Static/CoreIcon';
import StrategicIcon from '../../components/widgets/CoreScore/Static/StrategicIcon';
import ModerateRiskIcon from '../../components/widgets/CoreScore/Static/ModerateRiskIcon';
import SpeculativeIcon from '../../components/widgets/CoreScore/Static/SpeculativeIcon';
import {
  COMPANY_DETAILS_COLUMNS,
  CORE_SCORE_COLUMNS,
  DEFAULT_COMPANY_COLUMNS,
  DefaultCompanyColumn,
  DefaultFieldPermission,
  EXCLUDED_COMPANY_ES_FIELDS,
  EXCLUDED_GROUP,
  RISK_PRODUCT_PREFIX,
  WEIGHTING_ICONS_MAP,
} from './Company.constants';
import Coverage from './Coverage';
import {
  hasCovenantsPermissions,
  hasLevel2Permissions,
  hasLevel3Permissions,
  hasNewsPermissions,
  hasPermissionToCoreScores,
  hasResearchPermissions,
} from '../../utils/permissionsUtils';
import { CoreScoresDto } from '../../services/dtos/coreScores/coreScores.dto';

export const getCompanyPageLink = (tagId: string): string => {
  const [tagType, tagValue] = tagId.split('/');
  return tagType === 'company' ? `/companies/${tagValue}` : `/municipals/${tagValue}`;
};

const getImpliedValue = (tagType: string, data?: IssuerListItemDto) =>
  data?.implied?.filter(({ type }) => tagType === type)?.map((impliedTag) => impliedTag.displayName);

export const impliedValueGetter: (tagType: string) => ValueGetterFunc<IssuerListItemDto> = (tagType) => (params) =>
  getImpliedValue(tagType, params.data)?.join(', ');

const tooltipValueGetter: (
  valueGetter: (data?: IssuerListItemDto) => string[] | undefined
) => (params: ITooltipParams<IssuerListItemDto>) => string | undefined = (valueGetter) => (params) => {
  const value = valueGetter(params.data);

  if ((value?.length || 0) > 0) {
    return value?.join(', ');
  }

  return undefined;
};

const scoresToolTipValueGetter = (coreScores: CoreScoresDto | undefined, scoreValue: string): string | undefined => {
  if (coreScores && scoreValue) {
    const cellText = getScoreState(coreScores[scoreValue], scoreValue);
    return cellText.includes(' ') ? cellText : undefined;
  }
  return undefined;
};

const renderScoreIcon = (scorecategory) => {
  // Define a mapping for icons
  const iconMap = {
    Core: <CoreIcon showtitle />,
    Strategic: <StrategicIcon showtitle />,
    'Moderate Risk': <ModerateRiskIcon showtitle />,
    Speculative: <SpeculativeIcon showtitle />,
  };

  return iconMap[scorecategory];
};

const getImpliedCreditValue = (data?: IssuerListItemDto) =>
  data?.implied
    ?.map((tag) => {
      switch (tag.tagId) {
        case 'tag/high_yield':
          return 'High Yield';
        case 'tag/special_situations':
        case 'tag/investment_grade':
          return tag.displayName;
        default:
          return '';
      }
    })
    .filter(Boolean);

export const impliedCreditValueGetter: ValueGetterFunc<IssuerListItemDto> = (params) =>
  getImpliedCreditValue(params.data)?.join(', ');

export const dateFormatter = (params: ValueFormatterParams<IssuerListItemDto>): string =>
  formatDate(params.value, DATE_FORMATS.MONTH_ABBR_DATE, EM_DASH);

const getColumnDefByRenderType = (column: RpColumn): Partial<ColDef<IssuerListItemDto>> => {
  const colDef = getDefaultRpColumnDefByRenderType(column);
  if (colDef) {
    return colDef;
  }
  switch (column.renderType) {
    case RpColumnRenderType.COMPANY_LINK:
      return {
        pinned: 'left',
        cellRenderer: ({ data, value }: { data: IssuerListItemDto; value: any }) =>
          value !== undefined ? (
            <NavLink to={getCompanyPageLink(data.tagId)}>{data.displayName}</NavLink>
          ) : (
            <div style={{ position: 'relative' }}>
              <Loader text="" />
            </div>
          ),
        comparator: agTableValueComparator,
      };
    case RpColumnRenderType.CS_VIEW:
      return {
        valueGetter: (params) => (params.data?.csCoverage ? generateCSViewString(params) : null),
        cellRenderer: ({ data, value }) =>
          value ? (
            <Icon name={WEIGHTING_ICONS_MAP[data!.latestRec!.weighting.toLowerCase()]} height={20} width={20} />
          ) : (
            EM_DASH
          ),
      };
    case RpColumnRenderType.CORE_SCORE:
      return {
        valueGetter: (params) => {
          if (params.data?.coreScores?.assessment) {
            const score = fetchScoresCategory(params.data?.coreScores?.assessment, 1);
            return score.score;
          }
          return EM_DASH;
        },
        cellRenderer: (params) => {
          const score = params.data?.coreScores?.assessment || EM_DASH;
          const outlookNumber = params.data?.coreScores?.outlook || EM_DASH;
          const { score: mappedScore } = fetchScoresCategory(score, outlookNumber);
          if (mappedScore) {
            return renderScoreIcon(mappedScore);
          }
          return EM_DASH;
        },
        sortable: true,
        suppressSizeToFit: true,
        minWidth: 140,
        width: 130,
        cellStyle: {},
        resizable: true,
        suppressMovable: false,
      };
    case RpColumnRenderType.CORE_SCORE_OUTLOOK:
      return {
        valueGetter: (params) => {
          if (params.data?.coreScores?.outlook) {
            const score = fetchScoresCategory(1, params.data?.coreScores?.outlook);
            return score.outlook;
          }
          return EM_DASH;
        },
        cellRenderer: (params) => {
          const outlook = params.data?.coreScores?.outlook;
          const score = params.data?.coreScores?.assessment || EM_DASH;
          const { outlook: mappedScore } = fetchScoresCategory(score, outlook);
          if (mappedScore) {
            return renderScoreIcon(mappedScore);
          }
          return EM_DASH;
        },
        sortable: true,
        suppressSizeToFit: true,
        minWidth: 140,
        width: 130,
        cellStyle: {},
        resizable: true,
        suppressMovable: false,
      };

    case RpColumnRenderType.IMPLIED:
      return {
        valueGetter: impliedValueGetter(column.es_field),
        tooltipValueGetter: tooltipValueGetter((data) => getImpliedValue(column.es_field, data)),
        sortable: column.meta?.sortable ?? true,
      };
    case RpColumnRenderType.CREDIT:
      return {
        valueGetter: impliedCreditValueGetter,
        tooltipValueGetter: tooltipValueGetter((data) => getImpliedCreditValue(data)),
      };
    case RpColumnRenderType.COVERAGE:
      return {
        cellRenderer: (params) => (
          <Coverage
            csCoverage={params.data?.csCoverage}
            crCoverage={params.data?.crCoverage}
            lfiCoverage={params.data?.lfiCoverage}
            financialsCoverage={params.data?.financialsCoverage}
            riskProductsCoverage={params.data?.riskProductsCoverage}
          />
        ),
        sortable: false,
        suppressSizeToFit: true,
        minWidth: 80,
        width: 130,
        valueGetter: generateCoverageString,
      };
    case RpColumnRenderType.SELECTION:
      return {
        valueFormatter: () => '',
        colId: 'selection',
        lockPosition: 'left',
        pinned: 'left',
        checkboxSelection: true,
        resizable: false,
        suppressMovable: true,
        maxWidth: 24,
        // headerCheckboxSelection: true,
        headerClass: 'selection-cell',
        cellClass: 'selection-cell',
        sortable: false,
      };
    case RpColumnRenderType.SCORE:
      return {
        valueGetter: (params) =>
          params.data?.coreScores ? getScoreString(params.data?.coreScores, column.colId!) : EM_DASH,
        sortable: false,
        tooltipValueGetter: (params) => scoresToolTipValueGetter(params.data?.coreScores, column.colId!),
      };
    case RpColumnRenderType.DEFAULT:
    default:
      return {
        tooltipValueGetter: (params) => (typeof params.value === 'string' ? params.value : null),
      };
  }
};

export const mapRpColumnToDefinition = (column: RpColumn): ColDef<IssuerListItemDto> => ({
  refData: { group: column.group },
  colId: column.colId ?? column.es_field,
  field: column.es_field,
  headerName: column.short_name ?? column.data_field,
  headerTooltip: column.description,
  suppressMenu: true,
  hide: !column.canAccess,
  ...getColumnDefByRenderType(column),
});

export const getColDefForColumns = (esFields: EsField[], columns: RpColumn[]): ColDef<IssuerListItemDto>[] => {
  return columns.filter((column) => esFields.includes(column.es_field)).map(mapRpColumnToDefinition);
};

type ComparatorFunction = (a: ColDef<IssuerListItemDto>, b: ColDef<IssuerListItemDto>) => number;

const companyColumnsComparatorGet = (userPermissions?: UserPermissions): ComparatorFunction => {
  const companyColumns: EsField[] = defaultCompanyColumnsGet(userPermissions);
  return (a: ColDef<IssuerListItemDto>, b: ColDef<IssuerListItemDto>): number => {
    const indexA = companyColumns.indexOf(a.colId as EsField);
    const indexB = companyColumns.indexOf(b.colId as EsField);
    return indexA - indexB;
  };
};

const defaultCompanyColumnPermissionsCheck = (
  defaultCompanyColumn: DefaultCompanyColumn,
  userPermissions: UserPermissions
): boolean => {
  let hasAcess = false;
  if (defaultCompanyColumn.allPermissions) {
    hasAcess = true;
  } else if (defaultCompanyColumn.permissions) {
    for (let i = 0; i < defaultCompanyColumn.permissions.length; i += 1) {
      const permission: DefaultFieldPermission = defaultCompanyColumn.permissions[i];
      if (permission === PermissionEnum.RESEARCH) {
        hasAcess = hasResearchPermissions(userPermissions);
      } else if (permission === PermissionEnum.COVENANTS) {
        hasAcess = hasCovenantsPermissions(userPermissions);
      } else if (permission === PermissionEnum.NEWS) {
        hasAcess = hasNewsPermissions(userPermissions);
      } else if (permission === PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_2) {
        hasAcess = hasLevel2Permissions(userPermissions);
      } else if (permission === PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3) {
        hasAcess = hasLevel3Permissions(userPermissions);
      }
      if (hasAcess) {
        break;
      }
    }
  }
  return hasAcess;
};

const defaultCompanyColumnsGet = (userPermissions?: UserPermissions): EsField[] => {
  let companyColumns: EsField[] = [];
  if (userPermissions) {
    companyColumns = DEFAULT_COMPANY_COLUMNS.filter((defaultCompanyColumn: DefaultCompanyColumn) => {
      return defaultCompanyColumnPermissionsCheck(defaultCompanyColumn, userPermissions);
    }).map((defaultCompanyColumn: DefaultCompanyColumn) => defaultCompanyColumn.fieldName);
  }
  return companyColumns;
};

export const getInitialCompanyColumns = (
  allColumnDefinitions: ColDef<IssuerListItemDto>[],
  userPermissions?: UserPermissions
): ColDef<IssuerListItemDto>[] => {
  const defaultCompanyColumns: EsField[] = defaultCompanyColumnsGet(userPermissions);
  const columnsComparator: ComparatorFunction = companyColumnsComparatorGet(userPermissions);
  const defaultCompanyColumnDefs: ColDef<IssuerListItemDto>[] = allColumnDefinitions
    .filter((column) => defaultCompanyColumns.includes(column.colId as string))
    .sort(columnsComparator);
  return defaultCompanyColumnDefs;
};

export const prepareRPColumnsForCompany = (rpColumns: RpColumn[], userPermissions?: UserPermissions): RpColumn[] => {
  return COMPANY_DETAILS_COLUMNS.filter((column) => {
    const defColumn = DEFAULT_COMPANY_COLUMNS.find(
      (defColumn) => defColumn.fieldName === (column.colId || column.es_field)
    );

    if (defColumn && userPermissions) {
      return defaultCompanyColumnPermissionsCheck(defColumn, userPermissions);
    }

    return true;
  })
    .concat(
      rpColumns
        .filter((item) => item.group !== EXCLUDED_GROUP && !EXCLUDED_COMPANY_ES_FIELDS.includes(item.es_field))
        .map((column) => {
          return {
            ...column,
            es_field: `${RISK_PRODUCT_PREFIX}.${column.es_field}`,
            meta: column.meta
              ? {
                  ...column.meta,
                  previousField: column.meta.previousField
                    ? `${RISK_PRODUCT_PREFIX}.${column.meta.previousField}`
                    : undefined,
                }
              : undefined,
          };
        })
    )
    .concat(hasPermissionToCoreScores(userPermissions) ? CORE_SCORE_COLUMNS : []);
};

export const getScoreString = (coreScores: CoreScoresDto, scoreValue: string): string => {
  if (coreScores && scoreValue) {
    return getScoreState(coreScores[scoreValue], scoreValue);
  }
  return EM_DASH;
};

export const generateCoverageString: ValueGetterFunc<IssuerListItemDto> = (params): string => {
  const coverageArray: string[] = [];
  if (params.data?.csCoverage) {
    coverageArray.push(ContentType.Research);
  }
  if (params.data?.crCoverage) {
    coverageArray.push(ContentType.Covenants);
  }
  if (params?.data?.lfiCoverage) {
    coverageArray.push(ContentType.News);
  }
  if (params?.data?.financialsCoverage) {
    coverageArray.push(ContentType.Financials);
  }
  if (params?.data?.riskProductsCoverage) {
    coverageArray.push(ContentType.RiskProducts);
  }
  return coverageArray.join(', ');
};

export const generateCSViewString: ValueGetterFunc<IssuerListItemDto> = (params): string => {
  const { weighting } = params.data!.latestRec!;
  return CS_VIEW_DATA.find((d) => d.key === weighting)?.title ?? weighting;
};

export const processCellForExport = (params: ProcessCellForExportParams): string => {
  const colDef = params.column.getColDef();
  const nextParams: ValueFormatterParams = {
    ...params,
    data: params.node?.data,
    node: params.node!,
    colDef,
  };

  if (colDef.valueFormatter) {
    return (colDef.valueFormatter as ValueFormatterFunc<IssuerDto>)(nextParams);
  }

  return nextParams.value;
};

export const createExportFileName = (): string => `Companies_${formatDate(new Date(), DATE_FORMATS.YEAR_MONTH_DAY)}`;

export const mapSortField = (field: string): string => {
  switch (field) {
    case `${RISK_PRODUCT_PREFIX}.acr`:
    case `${RISK_PRODUCT_PREFIX}.cqs_trend_1y`:
    case `${RISK_PRODUCT_PREFIX}.cqs_trend_3y`:
    case `${RISK_PRODUCT_PREFIX}.cqs_volatility`:
    case `${RISK_PRODUCT_PREFIX}.credit_risk_level`:
    case `${RISK_PRODUCT_PREFIX}.credit_risk_trend_1y`:
    case `${RISK_PRODUCT_PREFIX}.credit_risk_trend_6m`:
    case `${RISK_PRODUCT_PREFIX}.cqs_outlook`:
      return `${field}_num`;
    default:
      return field;
  }
};
